import React, { useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
    useMediaFiles,
    useCreateMediaFile,
    useUpdateMediaFile,
    useDeleteMediaFile,
} from '../../hooks/mediaFiles';
import mediaFilesService from '../../api/mediaFilesService';
import type { MediaFile } from '../../types/types';
import { t } from '../../i18n/t';
import MediaFilePreview, { mediaKindFromMime } from './MediaFilePreview';

const ALLOWED_EXT = /\.(svg|png|jpe?g|webp|gif|mp3|wav|ogg|m4a|mp4|webm|mov)$/i;
const ACCEPT =
    'image/*,video/*,audio/*,.svg,.mp3,.wav,.ogg,.m4a,.mp4,.webm,.mov';

const formatDate = (s: string) =>
    new Date(s).toLocaleString(navigator.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

const DashboardMediaFiles = () => {
    const { data: files = [], isLoading, refetch } = useMediaFiles();
    const deleteMutation = useDeleteMediaFile();
    const [uploadOpen, setUploadOpen] = useState(false);
    const [editingFile, setEditingFile] = useState<MediaFile | null>(null);

    const handleDelete = async (file: MediaFile) => {
        if (!window.confirm(t('mediaFiles.deleteConfirm', { name: file.name }))) return;
        try {
            await deleteMutation.mutateAsync(file.id);
        } catch (e) {
            console.error(e);
            alert(t('mediaFiles.deleteFailed'));
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('mediaFiles.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="contained" onClick={() => setUploadOpen(true)}>
                    {t('mediaFiles.upload')}
                </Button>
                <Tooltip title={t('mediaFiles.refresh')}>
                    <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('mediaFiles.colPreview')}</TableCell>
                            <TableCell>ID</TableCell>
                            <TableCell>{t('mediaFiles.colName')}</TableCell>
                            <TableCell>{t('mediaFiles.colType')}</TableCell>
                            <TableCell>{t('mediaFiles.colFilename')}</TableCell>
                            <TableCell>{t('mediaFiles.colMime')}</TableCell>
                            <TableCell>{t('mediaFiles.colCreated')}</TableCell>
                            <TableCell align="right">{t('mediaFiles.colActions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {files.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    {t('mediaFiles.empty')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            files.map((file) => (
                                <TableRow key={file.id}>
                                    <TableCell>
                                        <MediaFilePreview
                                            src={mediaFilesService.getFileUrl(file.id)}
                                            mimeType={file.mimeType}
                                            alt={file.name}
                                            compact
                                            sx={{ border: 1, borderColor: 'divider' }}
                                        />
                                    </TableCell>
                                    <TableCell>{file.id}</TableCell>
                                    <TableCell>{file.name}</TableCell>
                                    <TableCell>
                                        {t(`mediaFiles.kind.${mediaKindFromMime(file.mimeType)}`)}
                                    </TableCell>
                                    <TableCell>{file.originalFilename ?? '—'}</TableCell>
                                    <TableCell>{file.mimeType}</TableCell>
                                    <TableCell>{formatDate(file.created_at)}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={t('common.edit')}>
                                            <IconButton size="small" onClick={() => setEditingFile(file)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('common.delete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(file)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {uploadOpen && <UploadMediaDialog onClose={() => setUploadOpen(false)} />}
            {editingFile && (
                <EditMediaDialog file={editingFile} onClose={() => setEditingFile(null)} />
            )}
        </Box>
    );
};

const UploadMediaDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const createMutation = useCreateMediaFile();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [name, setName] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (!f) return;
        if (!ALLOWED_EXT.test(f.name)) {
            alert(t('mediaFiles.invalidFormat'));
            return;
        }
        setFile(f);
        if (!name.trim()) {
            setName(f.name.replace(/\.[^.]+$/, ''));
        }
    };

    const handleSave = async () => {
        if (!name.trim()) {
            alert(t('mediaFiles.enterName'));
            return;
        }
        if (!file) {
            alert(t('mediaFiles.pickFile'));
            return;
        }
        setSaving(true);
        try {
            await createMutation.mutateAsync({ name: name.trim(), file });
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('mediaFiles.uploadFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>{t('mediaFiles.uploadDialog')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label={t('mediaFiles.fieldName')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        size="small"
                        fullWidth
                    />
                    <Button
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {file ? file.name : t('mediaFiles.pickFile')}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                        {t('mediaFiles.formatsHint')}
                    </Typography>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        hidden
                        onChange={handleFileChange}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const EditMediaDialog: React.FC<{ file: MediaFile; onClose: () => void }> = ({ file, onClose }) => {
    const updateMutation = useUpdateMediaFile();
    const [name, setName] = useState(file.name);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!name.trim()) {
            alert(t('mediaFiles.enterName'));
            return;
        }
        setSaving(true);
        try {
            await updateMutation.mutateAsync({ id: file.id, dto: { name: name.trim() } });
            onClose();
        } catch (e) {
            console.error(e);
            alert(t('mediaFiles.saveFailed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
            <DialogTitle>{t('mediaFiles.editDialog')}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <MediaFilePreview
                        src={mediaFilesService.getFileUrl(file.id)}
                        mimeType={file.mimeType}
                        alt={file.name}
                        sx={{
                            border: 1,
                            borderColor: 'divider',
                            alignSelf: 'flex-start',
                        }}
                    />
                    <TextField
                        label={t('mediaFiles.fieldName')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        size="small"
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DashboardMediaFiles;
